/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-disable @coze-arch/max-line-per-function, max-lines -- staged flat navigation shell exceeds legacy lint limits */
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { brandConfig } from 'brand-config';
import {
  IconCozArrowRight,
  IconCozBot,
  IconCozBotFill,
  IconCozCheckMark,
  IconCozClock,
  IconCozClockFill,
  IconCozDocument,
  IconCozDocumentFill,
  IconCozKnowledge,
  IconCozKnowledgeFill,
  IconCozMagnifier,
  IconCozPlusCircle,
  IconCozPlugin,
  IconCozPluginFill,
  IconCozSetting,
  IconCozSettingFill,
  IconCozSkill,
  IconCozStore,
  IconCozStoreFill,
  IconCozTemplate,
  IconCozTemplateFill,
} from '@coze-arch/coze-design/icons';
import {
  Avatar,
  Divider,
  IconButton,
  Input,
  Tooltip,
  Typography,
} from '@coze-arch/coze-design';
import { useSystemConfig } from '@adp-foundation/global-store';
import { useCreateBotAction } from '@adp-foundation/global';
import { SpaceRoleType } from '@adp-arch/idl/developer_api';
import { I18n } from '@adp-arch/i18n';
import { useIsSettled, useUserInfo } from '@adp-arch/foundation-sdk';
import { useSpaceList, useSpaceStore } from '@adp-arch/bot-studio-store';

import { AccountDropdown } from '../account-dropdown';

import s from './flat-nav-sider.module.less';

const STORAGE_KEY = 'global-flat-nav-collapsed';

interface FlatNavItem {
  key: string;
  title: string;
  path: string;
  icon: ReactNode;
  activeIcon?: ReactNode;
  reloadDocument?: boolean;
  activeClassName?: string;
  match: (pathname: string) => boolean;
  dataTestId: string;
}

const getCollapsedFromStorage = () =>
  typeof window !== 'undefined' &&
  window.localStorage.getItem(STORAGE_KEY) === '1';

const setCollapsedToStorage = (collapsed: boolean) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }
};

const getNavLinkClassName = (
  active: boolean,
  collapsed: boolean,
  activeClassName?: string,
) => {
  const baseClassName = `${s.link} group flex items-center rounded-[8px] no-underline coz-fg-primary hover:coz-mg-secondary-hovered`;
  const sizeClassName = collapsed
    ? 'justify-center w-[44px] h-[44px] mx-auto'
    : 'gap-[10px] w-full h-[40px] px-[12px]';
  const activeStateClassName = active
    ? `${s.active} coz-bg-primary coz-fg-plus ${activeClassName ?? ''}`.trim()
    : 'coz-bg-max';

  return `${baseClassName} ${sizeClassName} ${activeStateClassName}`;
};

const FlatNavLink = ({
  item,
  collapsed,
}: {
  item: FlatNavItem;
  collapsed: boolean;
}) => {
  const location = useLocation();
  const active = item.match(location.pathname);

  const content = (
    <NavLink
      to={item.path}
      reloadDocument={item.reloadDocument}
      className={getNavLinkClassName(active, collapsed, item.activeClassName)}
      data-testid={item.dataTestId}
      aria-label={item.title}
    >
      <div className="text-[18px] leading-none flex items-center justify-center">
        {active ? (item.activeIcon ?? item.icon) : item.icon}
      </div>
      {collapsed ? null : (
        <span className="text-[14px] leading-[20px] font-[500] truncate">
          {item.title}
        </span>
      )}
    </NavLink>
  );

  if (!collapsed) {
    return content;
  }

  return (
    <Tooltip content={item.title} position="right">
      {content}
    </Tooltip>
  );
};

const SectionDivider = ({ collapsed }: { collapsed: boolean }) => (
  <Divider className={collapsed ? 'my-[10px] w-[28px] mx-auto' : 'my-[12px]'} />
);

const DoorCollapseIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="7" height="16" rx="1.5" />
    <path d={collapsed ? 'M17 8l4 4-4 4' : 'M21 8l-4 4 4 4'} />
    <path d="M10 4h9a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-9" />
  </svg>
);

const getFlatNavBrandTitle = (dynamicProductName?: string) => {
  const displayName =
    dynamicProductName?.trim() || brandConfig.displayName?.trim() || 'ADP';
  const normalizedBrandName = displayName.replace(/\s+ADP$/i, '');

  return normalizedBrandName;
};

const getTemplateStoreTitle = () =>
  I18n.t(
    'navigation_template_store',
    {},
    I18n.language?.startsWith('zh') ? '模板商店' : 'Template Store',
  );

const getStoredWorkspaceSpaceId = () =>
  typeof window !== 'undefined'
    ? window.localStorage.getItem('workspace-spaceId')
    : null;

const resolveActiveSpace = <TSpace extends { id?: string }>(
  currentSpace: TSpace | undefined,
  spaces: TSpace[],
) => {
  if (currentSpace?.id) {
    return spaces.find(space => space.id === currentSpace.id) ?? currentSpace;
  }

  const storedSpaceId = getStoredWorkspaceSpaceId();
  if (storedSpaceId) {
    const storedSpace = spaces.find(space => space.id === storedSpaceId);
    if (storedSpace) {
      return storedSpace;
    }
  }

  return spaces[0];
};

const getSpaceRoleLabel = (roleType?: SpaceRoleType) => {
  switch (roleType) {
    case SpaceRoleType.Owner:
      return I18n.t('team_management_role_owner', {}, '所有者');
    case SpaceRoleType.Admin:
      return I18n.t('space_role_admin', {}, '管理员');
    case SpaceRoleType.Member:
      return I18n.t('space_role_member', {}, '成员');
    default:
      return '';
  }
};

const WorkspaceSwitcher = () => {
  const navigate = useNavigate();
  const { spaces = [], loading } = useSpaceList(true);
  const currentSpace = useSpaceStore(state => state.space);
  const [searchValue, setSearchValue] = useState('');
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible]);

  const filteredSpaces = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    return spaces.filter(space => {
      if (space.hide_operation) {
        return false;
      }
      const idValue = String(space.id ?? '').toLowerCase();
      const nameValue = space.name?.toLowerCase() ?? '';
      return (
        !keyword || nameValue.includes(keyword) || idValue.includes(keyword)
      );
    });
  }, [spaces, searchValue]);

  const displaySpace = useMemo(
    () =>
      resolveActiveSpace(
        currentSpace,
        filteredSpaces.length > 0 ? filteredSpaces : spaces,
      ),
    [currentSpace, filteredSpaces, spaces],
  );

  const recentSpaces = useMemo(() => {
    const currentSpaceItem = filteredSpaces.find(
      space => space.id === displaySpace?.id,
    );
    const otherSpaces = filteredSpaces.filter(
      space => space.id !== displaySpace?.id,
    );
    return currentSpaceItem
      ? [currentSpaceItem, ...otherSpaces.slice(0, 4)]
      : filteredSpaces.slice(0, 5);
  }, [displaySpace?.id, filteredSpaces]);

  const allSpaces = filteredSpaces;

  const renderSpaceItem = (
    space: (typeof filteredSpaces)[number],
    prefix: string,
  ) => {
    const isActive = displaySpace?.id === space.id;

    return (
      <button
        key={`${prefix}-${space.id}`}
        type="button"
        className={`w-full flex items-center px-3 py-3 border-0 cursor-pointer text-left transition-colors bg-transparent ${
          isActive
            ? 'bg-[var(--coz-bg-tertiary)]'
            : 'hover:bg-[var(--coz-bg-tertiary)]'
        }`}
        onClick={() => {
          if (!space.id) {
            return;
          }
          setVisible(false);
          window.localStorage.setItem('workspace-spaceId', String(space.id));
          navigate(`/space/${space.id}`);
        }}
        data-testid={`workspace-switcher-item-${space.id}`}
      >
        <div className="w-5 mr-2 flex justify-center">
          {isActive ? <IconCozCheckMark /> : null}
        </div>
        <Avatar
          size="small"
          src={(space as { icon_url?: string }).icon_url}
          name={space.name ?? ''}
          className="mr-3"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] leading-[20px] font-[500]">
            {space.name}
          </div>
        </div>
        <div className="ml-3 shrink-0 text-[12px] leading-[16px] coz-fg-secondary">
          {getSpaceRoleLabel(space.space_role_type)}
        </div>
      </button>
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="w-full px-[14px] py-[12px] h-[64px] bg-transparent border border-solid coz-stroke-primary cursor-pointer flex items-center text-left hover:bg-[var(--coz-bg-plus)] transition-colors rounded-[16px]"
        onClick={() => setVisible(prev => !prev)}
        data-testid="workspace-switcher"
      >
        <Avatar
          size="small"
          src={displaySpace?.icon_url}
          name={displaySpace?.name ?? ''}
          className="mr-3"
        />
        <div
          className="flex-1 min-w-0 text-[16px] leading-[24px] font-[600] truncate"
          title={
            displaySpace?.name ||
            I18n.t('menu_title_personal_space', {}, 'Personal Space')
          }
        >
          {displaySpace?.name ||
            I18n.t('menu_title_personal_space', {}, 'Personal Space')}
        </div>
        <IconCozArrowRight
          className={`ml-2 transition-transform ${visible ? 'rotate-90' : ''}`}
        />
      </button>
      {visible ? (
        <div className="absolute left-0 top-[calc(100%+8px)] z-20 w-[560px] max-w-[calc(100vw-64px)] flex flex-col max-h-[520px] coz-bg-max rounded-[24px] shadow-lg border border-solid coz-stroke-primary overflow-hidden">
          <div className="px-3 pt-3 pb-2">
            <Input
              prefix={<IconCozMagnifier />}
              placeholder={I18n.t(
                'search_workspace_placeholder',
                {},
                'Search workspace',
              )}
              value={searchValue}
              onChange={value => setSearchValue(value)}
            />
          </div>
          <div className="flex-1 overflow-y-auto min-h-[180px]">
            {loading ? (
              <div className="px-3 py-8 text-center">
                <Typography.Text type="tertiary">Loading...</Typography.Text>
              </div>
            ) : filteredSpaces.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <Typography.Text type="tertiary">
                  {I18n.t('workspace_switcher_empty', {}, 'No workspace found')}
                </Typography.Text>
              </div>
            ) : (
              <>
                <div className="px-3 py-1 text-xs text-[var(--coz-fg-tertiary)]">
                  {I18n.t('workspace_list_recent', {}, 'Recent')}
                </div>
                {recentSpaces.map(space => renderSpaceItem(space, 'recent'))}
                <div className="mt-2 px-3 py-1 text-xs text-[var(--coz-fg-tertiary)]">
                  {I18n.t('workspace_switcher_role_all', {}, '全部')}
                </div>
                {allSpaces.map(space => renderSpaceItem(space, 'all'))}
              </>
            )}
          </div>
          <div className="px-4 py-3 border-t border-solid coz-stroke-primary">
            <button
              type="button"
              disabled
              className="w-full text-left border-0 bg-transparent coz-fg-dim cursor-not-allowed text-[14px] leading-[20px] font-[500]"
              data-testid="workspace-switcher-create-disabled"
            >
              + {I18n.t('create_new_workspace', {}, 'Create new workspace')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const FlatNavSider = () => {
  const { space_id } = useParams();
  const location = useLocation();
  const userInfo = useUserInfo();
  const isUserSettled = useIsSettled();
  const currentSpace = useSpaceStore(state => state.space);
  const { spaces = [] } = useSpaceList(true);
  const [collapsed, setCollapsed] = useState(
    () =>
      getCollapsedFromStorage() ||
      !!window.matchMedia?.('(max-width: 960px)').matches,
  );

  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 960px)');
    const collapseOnNarrowScreen = () => {
      if (media?.matches) {
        setCollapsed(true);
      } else {
        setCollapsed(getCollapsedFromStorage());
      }
    };
    media?.addEventListener('change', collapseOnNarrowScreen);
    return () => media?.removeEventListener('change', collapseOnNarrowScreen);
  }, []);
  const { brandConfiguration, enableSkill, enableADPLoop } = useSystemConfig();

  const resolvedSpace = useMemo(
    () => resolveActiveSpace(currentSpace, spaces),
    [currentSpace, spaces],
  );

  const currentSpaceId = space_id ?? resolvedSpace?.id;
  const isEndUser = isUserSettled && userInfo?.user_type === 'end_user';
  const accountName =
    userInfo?.app_user_info?.user_unique_name ?? userInfo?.name ?? '';
  const hasSpaceManagePermission =
    resolvedSpace?.space_role_type === SpaceRoleType.Owner ||
    resolvedSpace?.space_role_type === SpaceRoleType.Admin;

  const { createBot, createBotModal } = useCreateBotAction({
    currentSpaceId,
  });

  const getSpacePath = (subPath: string) =>
    currentSpaceId ? `/space/${currentSpaceId}/${subPath}` : '/space';

  const upperItems = useMemo<FlatNavItem[]>(() => {
    if (isEndUser) {
      return [];
    }

    const items: FlatNavItem[] = [
      {
        key: 'develop',
        title: I18n.t('navigation_workspace_develop', {}, '项目开发'),
        path: getSpacePath('develop'),
        icon: <IconCozBot />,
        activeIcon: <IconCozBotFill />,
        match: pathname => pathname.includes('/develop'),
        dataTestId: 'flat-nav-develop',
      },
    ];

    if (hasSpaceManagePermission) {
      items.push(
        {
          key: 'library',
          title: I18n.t('navigation_workspace_library', {}, '资源库'),
          path: getSpacePath('library'),
          icon: <IconCozKnowledge />,
          activeIcon: <IconCozKnowledgeFill />,
          match: pathname => pathname.includes('/library'),
          dataTestId: 'flat-nav-library',
        },
        {
          key: 'chat-history',
          title: I18n.t('navigation_workspace_chat_history', {}, '聊天历史'),
          path: getSpacePath('chat_history'),
          icon: <IconCozClock />,
          activeIcon: <IconCozClockFill />,
          match: pathname =>
            pathname.includes('/chat_history') ||
            pathname.includes('/log_management'),
          dataTestId: 'flat-nav-chat-history',
        },
        {
          key: 'token-usage',
          title: I18n.t('navigation_workspace_token_usage', {}, 'Token Usage'),
          path: getSpacePath('token_usage'),
          icon: <IconCozDocument />,
          activeIcon: <IconCozDocumentFill />,
          match: pathname => pathname.includes('/token_usage'),
          dataTestId: 'flat-nav-token-usage',
        },
        {
          key: 'team',
          title: I18n.t('navigation_workspace_team', {}, '空间设置'),
          path: getSpacePath('team'),
          icon: <IconCozSetting />,
          activeIcon: <IconCozSettingFill />,
          match: pathname => pathname.includes('/team'),
          dataTestId: 'flat-nav-team',
        },
      );
    }

    if (currentSpaceId && enableADPLoop) {
      items.push({
        key: 'loop',
        title: I18n.t('navigation_workspace_loop', {}, '评测'),
        path: `/loop/console/enterprise/personal/space/${encodeURIComponent(currentSpaceId)}/pe/prompts`,
        reloadDocument: true,
        icon: <IconCozMagnifier />,
        match: () => false,
        dataTestId: 'flat-nav-loop',
      });
    }

    return items;
  }, [currentSpaceId, hasSpaceManagePermission, isEndUser, enableADPLoop]);

  const lowerItems = useMemo<FlatNavItem[]>(() => {
    const items: FlatNavItem[] = [
      {
        key: 'store',
        title: I18n.t('menu_store_bots', {}, 'Agent Store'),
        path: '/explore/store',
        icon: <IconCozStore />,
        activeIcon: <IconCozStoreFill />,
        match: pathname => pathname.startsWith('/explore/store'),
        dataTestId: 'flat-nav-store',
      },
    ];

    if (!isEndUser && ENABLE_EXPLORE_PLUGIN) {
      items.push({
        key: 'plugin',
        title: I18n.t('menu_store_plugins', {}, 'Plugin Store'),
        path: '/explore/plugin',
        icon: <IconCozPlugin />,
        activeIcon: <IconCozPluginFill />,
        match: pathname => pathname.startsWith('/explore/plugin'),
        dataTestId: 'flat-nav-plugin',
      });
    }

    if (!isEndUser && enableSkill) {
      items.push({
        key: 'skill',
        title: I18n.t('skill_store_title', {}, 'Skill Store'),
        path: '/explore/skill',
        icon: <IconCozSkill />,
        match: pathname => pathname.startsWith('/explore/skill'),
        dataTestId: 'flat-nav-skill',
      });
    }

    if (!isEndUser) {
      items.push({
        key: 'template',
        title: getTemplateStoreTitle(),
        path: '/explore/template',
        icon: <IconCozTemplate />,
        activeIcon: <IconCozTemplateFill />,
        match: pathname => pathname.startsWith('/explore/template'),
        dataTestId: 'flat-nav-template',
      });
    }

    if (!isEndUser) {
      items.push({
        key: 'docs',
        title: I18n.t(
          'navigation_document_center',
          {},
          I18n.language === 'en' ? 'Documents' : '文档中心',
        ),
        path: '/docs',
        icon: <IconCozDocument />,
        activeIcon: <IconCozDocumentFill />,
        match: pathname => pathname.startsWith('/docs'),
        dataTestId: 'flat-nav-docs',
      });
    }

    return items;
  }, [enableSkill, isEndUser]);

  const toggleCollapsed = () => {
    const nextValue = !collapsed;
    setCollapsed(nextValue);
    setCollapsedToStorage(nextValue);
  };

  const expandedOnExplore =
    !collapsed && location.pathname.startsWith('/explore');
  const brandTitle = getFlatNavBrandTitle(brandConfiguration?.product_name);
  const brandLogoUrl =
    brandConfiguration?.logo_url?.trim() || brandConfig.logos.menuLogo;

  return (
    <>
      <div className={s.container}>
        <div
          className={`${s.shell} ${collapsed ? s.collapsed : ''} relative h-full coz-bg-max flex flex-col items-stretch`}
          style={{ width: collapsed ? 72 : 232 }}
          data-testid="flat-nav-sider"
        >
          <div
            className={collapsed ? 'px-[8px] pt-[12px]' : 'px-[12px] pt-[12px]'}
          >
            <div
              className={
                collapsed
                  ? 'flex justify-center'
                  : 'flex items-center justify-between gap-[8px]'
              }
            >
              {collapsed ? null : (
                <div
                  className="min-w-0 flex-1 text-left"
                  data-testid="flat-nav-brand-title"
                >
                  <div className="truncate text-[18px] leading-[28px] font-[700] tracking-[-0.02em] coz-fg-primary flex items-center gap-[8px]">
                    <img
                      src={brandLogoUrl}
                      alt={`${brandTitle} logo`}
                      className="w-[28px] h-[28px] flex-none object-contain"
                      data-testid="flat-nav-brand-logo"
                    />
                    {brandTitle}
                  </div>
                </div>
              )}
              <Tooltip
                content={I18n.t(
                  collapsed
                    ? 'workspace_navigation_expand'
                    : 'workspace_navigation_collapse',
                )}
                position="right"
              >
                <IconButton
                  color="secondary"
                  size="large"
                  icon={<DoorCollapseIcon collapsed={collapsed} />}
                  onClick={toggleCollapsed}
                  aria-label={I18n.t(
                    collapsed
                      ? 'workspace_navigation_expand'
                      : 'workspace_navigation_collapse',
                  )}
                  aria-expanded={!collapsed}
                  data-testid="flat-nav-collapse-button"
                />
              </Tooltip>
            </div>

            {collapsed || isEndUser ? null : (
              <div className="mt-[10px] flex items-start gap-[8px]">
                <div className="min-w-0 flex-1 overflow-visible rounded-[12px] coz-bg-plus">
                  <WorkspaceSwitcher />
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-[8px] pb-[12px] pt-[8px]">
            {isEndUser ? null : (
              <>
                <button
                  type="button"
                  onClick={createBot}
                  aria-label={I18n.t(
                    'workspace_create_ocean_project',
                    {},
                    '创建',
                  )}
                  className={
                    collapsed
                      ? 'w-[44px] h-[44px] mx-auto rounded-[8px] border-0 bg-[var(--coz-fg-primary)] text-[var(--coz-bg-max)] flex items-center justify-center cursor-pointer transition-opacity hover:opacity-90'
                      : 'w-full h-[40px] rounded-[8px] border-0 bg-[var(--coz-fg-primary)] text-[var(--coz-bg-max)] flex items-center gap-[10px] px-[12px] cursor-pointer transition-opacity hover:opacity-90'
                  }
                  data-testid="flat-nav-create"
                >
                  <span className="text-[18px] leading-none flex items-center justify-center">
                    <IconCozPlusCircle />
                  </span>
                  {collapsed ? null : (
                    <span className="text-[14px] leading-[20px] font-[500] truncate">
                      {I18n.t('workspace_create_ocean_project', {}, '创建')}
                    </span>
                  )}
                </button>
                <div className="mt-[8px] flex flex-col gap-[4px]">
                  {upperItems.map(item => (
                    <FlatNavLink
                      key={item.key}
                      item={item}
                      collapsed={collapsed}
                    />
                  ))}
                </div>
              </>
            )}

            {upperItems.length > 0 && lowerItems.length > 0 ? (
              <SectionDivider collapsed={collapsed} />
            ) : null}

            <div className="flex flex-col gap-[4px]">
              {lowerItems.map(item => (
                <FlatNavLink key={item.key} item={item} collapsed={collapsed} />
              ))}
            </div>

            {expandedOnExplore ? <div className="h-[4px]" /> : null}
          </div>

          <div
            className={collapsed ? 'px-[8px] pb-[12px]' : 'px-[12px] pb-[12px]'}
          >
            <div
              className={
                collapsed
                  ? 'flex justify-center pt-[4px]'
                  : 'flex items-center pt-[4px] w-full'
              }
            >
              <AccountDropdown
                collapsed={collapsed}
                accountName={accountName}
              />
            </div>
          </div>
        </div>
      </div>
      {createBotModal}
    </>
  );
};
